'use client'

import { type FocusEvent, type MouseEvent, type ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import { LicenseMapFilterContext } from './license-map-filter-context'
import { LicenseMapTooltip } from './license-map-tooltip'
import type { LicenseMapCategory, LicenseMapCategoryLabels, LicenseMapJurisdiction } from './license-map-types'
import { useLicenseMapTooltip } from './use-license-map-tooltip'

export interface LicenseMapInteractiveProps {
  children: ReactNode
  /** Localized rows, keyed by abbr for the tooltip. Sent as props so the SVG stays static. */
  jurisdictions: LicenseMapJurisdiction[]
  categoryLabels: LicenseMapCategoryLabels
  viewSourceLabel: string
}

const stateFrom = (target: EventTarget | null): Element | null =>
  target instanceof Element ? target.closest('.state[data-abbr]') : null

const insideTooltip = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('.tip') !== null

// Client wrapper for the server-rendered license map (children = legend, SVG,
// notes, table). Like CoverageMapInteractive it re-implements the source
// graphic's inline <script>, but with delegated React handlers on the root
// instead of per-path listeners: each state <path class="state"> carries
// data-abbr, so one mouseover/focus handler serves all 51. The legend filter
// is exposed as `data-off` on the root and applied purely in CSS.
export const LicenseMapInteractive = ({
  children,
  jurisdictions,
  categoryLabels,
  viewSourceLabel,
}: LicenseMapInteractiveProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const tooltip = useLicenseMapTooltip(rootRef)
  const [off, setOff] = useState<LicenseMapCategory[]>([])
  const toggle = useCallback((category: LicenseMapCategory) => {
    setOff((prev) => (prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]))
  }, [])
  const filter = useMemo(() => ({ off, toggle }), [off, toggle])
  const byAbbr = useMemo(() => new Map(jurisdictions.map((row) => [row.abbr, row])), [jurisdictions])
  const row = tooltip.active ? byAbbr.get(tooltip.active.abbr) : undefined

  const onMouseOver = (e: MouseEvent) => {
    const el = stateFrom(e.target)
    if (el) {
      tooltip.showAt(el, e.clientX, e.clientY)
    }
  }
  const onMouseOut = (e: MouseEvent) => {
    if (stateFrom(e.target)) {
      tooltip.scheduleHide()
    }
  }
  // Tap/click a state to pin its tooltip (touch has no hover); click anywhere
  // else in the widget — except inside the tooltip — to dismiss it.
  const onClick = (e: MouseEvent) => {
    const el = stateFrom(e.target)
    if (el) {
      tooltip.showAt(el, e.clientX, e.clientY)
    } else if (!insideTooltip(e.target)) {
      tooltip.hide()
    }
  }
  const onFocus = (e: FocusEvent) => {
    const el = stateFrom(e.target)
    if (el) {
      tooltip.showFor(el)
    }
  }
  const onBlur = (e: FocusEvent) => {
    if (stateFrom(e.target)) {
      tooltip.hide()
    }
  }

  return (
    <LicenseMapFilterContext.Provider value={filter}>
      <div
        ref={rootRef}
        className="ht-license-map not-prose relative"
        data-off={off.join(' ')}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
        <LicenseMapTooltip
          anchor={tooltip.active}
          row={row}
          categoryLabels={categoryLabels}
          viewSourceLabel={viewSourceLabel}
          onMouseEnter={tooltip.cancelHide}
          onMouseLeave={tooltip.scheduleHide}
        />
      </div>
    </LicenseMapFilterContext.Provider>
  )
}
